import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";

function Shell({ children }) {
  const { t, lang, toggleLang } = useApp();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="absolute top-4" style={{ insetInlineEnd: "1rem" }}>
        <button onClick={toggleLang} className="rounded-xl border border-border px-3 py-1.5 text-xs font-semibold hover:bg-secondary">{lang === "en" ? "العربية" : "English"}</button>
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground text-xl font-bold">IT</div>
          <h1 className="text-xl font-bold">{t("brand")}</h1>
          <p className="text-sm text-muted-foreground">{t("app")}</p>
          <p className="mt-1 text-xs text-accent">{t("tagline")}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-xl">{children}</div>
      </div>
    </div>
  );
}

export function Login() {
  const { login, t } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <h2 className="flex items-center gap-2 text-lg font-semibold"><ShieldCheck className="h-5 w-5 text-accent" /> {t("signin")}</h2>
        {error && <div data-testid="login-error" className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
        <div>
          <label className="mb-1 block text-sm font-medium">{t("email")}</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">{t("password")}</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <button data-testid="login-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} {t("signin")}
        </button>
        <div className="flex justify-between text-xs text-muted-foreground">
          <Link to="/forgot-password" className="underline" data-testid="forgot-link">Forgot password?</Link>
          <span>Demo: auditor@itoo.demo / Demo#2026</span>
        </div>
      </form>
    </Shell>
  );
}

export function ForcePasswordChange() {
  const { setUser, logout } = useApp();
  const nav = useNavigate();
  const { t } = useApp();
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const [error, setError] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw }); await logout(); nav("/login"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4" data-testid="force-change-form">
        <h2 className="text-lg font-semibold">{t("newPassword")}</h2>
        <p className="text-sm text-muted-foreground">{t("forceChange")}</p>
        {error && <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
        <input data-testid="fc-current" type="password" placeholder={t("currentPassword")} required value={cur} onChange={(e) => setCur(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
        <input data-testid="fc-new" type="password" placeholder={t("newPassword")} required value={nw} onChange={(e) => setNw(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
        <button data-testid="fc-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 font-semibold text-primary-foreground disabled:opacity-50">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("update")}</button>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState(""); const [msg, setMsg] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => { e.preventDefault(); setLoading(true); try { const { data } = await api.post("/auth/forgot-password", { email }); setMsg(data.message); } catch { setMsg("If that email is registered, a reset link has been sent."); } finally { setLoading(false); } };
  return (<Shell><form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
    <h2 className="text-lg font-semibold">Reset password</h2>
    {msg && <div className="rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm">{msg}</div>}
    <input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
    <button disabled={loading} className="w-full rounded-xl bg-primary px-4 py-2.5 font-semibold text-primary-foreground disabled:opacity-50">{loading ? "…" : "Send reset link"}</button>
    <Link to="/login" className="block text-center text-xs text-muted-foreground underline">Back to sign in</Link>
  </form></Shell>);
}

export function Reset() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState(""); const [msg, setMsg] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => { e.preventDefault(); setLoading(true); try { await api.post("/auth/reset-password", { token: params.get("token"), new_password: pw }); nav("/login"); } catch (err) { setMsg(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); } };
  return (<Shell><form onSubmit={submit} className="space-y-4" data-testid="reset-form">
    <h2 className="text-lg font-semibold">Set new password</h2>
    {msg && <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">{msg}</div>}
    <input data-testid="reset-password" type="password" required placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm" />
    <button disabled={loading} className="w-full rounded-xl bg-primary px-4 py-2.5 font-semibold text-primary-foreground disabled:opacity-50">{loading ? "…" : "Reset password"}</button>
    <Link to="/login" className="block text-center text-xs text-muted-foreground underline">Back to sign in</Link>
  </form></Shell>);
}
